<template>
  <header class="header">
    <div class="container">
      <div class="header-content">
        <!-- Logo -->
        <NuxtLinkLocale to="/" @click="closeMobileMenu" style="cursor: default">
          <div class="logo">
            <img src="https://www-img.sanfu.com/production/logo_dark.png" alt="SANFU" class="logo-icon" />
          </div>
        </NuxtLinkLocale>

        <!-- Desktop Navigation -->
        <nav class="nav-desktop" :class="{ 'nav-open': mobileMenuOpen }">
          <ul class="nav-list">
            <li class="nav-item">
              <NuxtLinkLocale to="/" class="nav-link" @click="closeMobileMenu">
                {{ $t('navigation.home') }}
              </NuxtLinkLocale>
            </li>
            <li class="nav-item">
              <NuxtLinkLocale to="/about" class="nav-link" @click="closeMobileMenu">
                {{ $t('navigation.about') }}
              </NuxtLinkLocale>
            </li>
            <li class="nav-item">
              <NuxtLinkLocale to="/activities" class="nav-link" @click="closeMobileMenu">
                {{ $t('navigation.activities') }}
              </NuxtLinkLocale>
            </li>
            <li class="nav-item">
              <NuxtLinkLocale to="/stores" class="nav-link" @click="closeMobileMenu">
                {{ $t('navigation.stores') }}
              </NuxtLinkLocale>
            </li>
            <li class="nav-item">
              <NuxtLinkLocale to="/contact" class="nav-link" @click="closeMobileMenu">
                {{ $t('navigation.contact') }}
              </NuxtLinkLocale>
            </li>
          </ul>
        </nav>

        <!-- Language Switcher & Mobile Menu Button -->
        <div class="header-actions">
          <!-- Language Switcher -->
          <ClientOnly>
            <div class="language-switcher">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>

              <el-dropdown trigger="hover" placement="bottom" class="lang-select-web">
                <el-button type="text">{{ locales.find(e => e.code === locale)?.name }}</el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item v-for="locale in locales" :key="locale.code" @click="change(locale.code)">{{ locale.name }}</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              <select @change="change()" class="lang-select" v-model="locale">
                <option v-for="locale in locales" :value="locale.code" :key="locale.code">{{ locale.name }}</option>
              </select>
            </div>
          </ClientOnly>

          <!-- Mobile Menu Button -->
          <button class="mobile-menu-btn" @click="toggleMobileMenu" :aria-expanded="mobileMenuOpen" aria-label="Toggle menu">
            <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
            <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
            <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation Overlay -->
    <div class="mobile-overlay" :class="{ active: mobileMenuOpen }" @click="closeMobileMenu"></div>
  </header>
</template>

<script setup>
  import { ref, onMounted, onUnmounted } from 'vue'

  const mobileMenuOpen = ref(false)

  const toggleMobileMenu = () => {
    mobileMenuOpen.value = !mobileMenuOpen.value
    // 防止背景滚动
    if (mobileMenuOpen.value) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }

  const closeMobileMenu = () => {
    mobileMenuOpen.value = false
    document.body.style.overflow = ''
  }

  const { setLocale, locales, getBrowserLocale, getLocaleCookie } = useI18n()
  const locale = ref(useI18n().locale.value)

  // 监听 ESC 键关闭移动菜单
  const handleEsc = e => {
    if (e.key === 'Escape' && mobileMenuOpen.value) {
      closeMobileMenu()
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', handleEsc)
    // 移除注释并添加正确的浏览器语言检测逻辑
    // 只有在没有已设置的语言cookie时才使用浏览器语言检测
    const cookieLocale = getLocaleCookie()
    if (!cookieLocale) {
      const browserLocale = getBrowserLocale()
      // 如果浏览器语言是中文相关，则设置为中文
      if (browserLocale && (browserLocale.startsWith('zh') || browserLocale === 'zh-CN' || browserLocale === 'zh-TW')) {
        setLocale('zh')
        locale.value = 'zh'
      }
    }

    // 设置浏览器语言标识
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale.value
    }
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleEsc)
    document.body.style.overflow = ''
  })

  const change = event => {
    if (event) locale.value = event
    console.log('Changing locale to:', locale.value)
    setLocale(locale.value)
    // 设置语言cookie以便下次访问时记住用户的选择
    // 同时设置浏览器语言标识
    if (typeof navigator !== 'undefined' && navigator.language) {
      // 更新HTML标签的lang属性
      if (typeof document !== 'undefined') {
        document.documentElement.lang = locale.value
      }
    }
  }
</script>

<style scoped lang="less">
  .header {
    border-bottom: 1px solid #e5e7ebc0;
    position: sticky;
    top: 0;
    z-index: 50;
    backdrop-filter: blur(8px);
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.5) 100%);
    height: 70px;
    user-select: none;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--space-4);
  }

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 70px;
  }

  /* Logo Styles */
  .logo-link {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    text-decoration: none;
  }

  .logo-icon {
    width: 145px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    pointer-events: none;
    -webkit-user-drag: none;
  }

  /* Desktop Navigation */
  .nav-desktop {
    display: none;
  }

  .nav-list {
    display: flex;
    list-style: none;
    gap: var(--space-8);
    margin: 0;
    padding: 0;
  }

  .nav-link {
    color: var(--gray-700);
    font-weight: 500;
    font-size: var(--text-sm);
    padding: var(--space-2) 0;
    position: relative;
    transition: color var(--transition-fast);
  }

  .nav-link:hover {
    color: var(--primary);
  }

  .nav-link.router-link-active {
    color: var(--primary);
  }

  .nav-link.router-link-active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--brand-red), var(--brand-orange));
  }

  /* Header Actions */
  .header-actions {
    align-self: stretch;
    display: flex;
    align-items: center;
    gap: var(--space-4);
    width: 145px;
    justify-content: flex-end;
  }

  /* Language Switcher */
  .language-switcher {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .lang-select-web {
    :deep(.el-button) {
      height: 36px;
      background: transparent;
      border: unset;
      color: #374151;
      justify-content: flex-start;
      &:hover {
        border: unset;
      }
      &:focus {
        outline: none;
        border: unset;
      }
    }
  }
  .lang-select {
    display: none;
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--gray-300);
    border-radius: var(--radius);
    background-color: #ffffff90;
    font-size: var(--text-sm);
    color: var(--gray-700);
    cursor: pointer;
    transition: border-color var(--transition-fast);
    width: 100px;
  }

  .lang-select:hover {
    border-color: var(--primary);
  }

  .lang-select:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(230, 0, 18, 0.1);
  }

  /* Mobile Menu Button */
  .mobile-menu-btn {
    flex-shrink: 0;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    width: 30px;
    padding: 0 3px;
    background: transparent;
    border: none;
    cursor: pointer;
  }

  .hamburger-line {
    width: 100%;
    height: 3px;
    background-color: var(--gray-700);
    transition: all var(--transition-fast);
    transform-origin: center;
  }

  .hamburger-line.active:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }

  .hamburger-line.active:nth-child(2) {
    opacity: 0;
  }

  .hamburger-line.active:nth-child(3) {
    transform: rotate(-45deg) translate(7px, -6px);
  }

  /* Mobile Overlay */
  .mobile-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 40;
    opacity: 0;
    transition: opacity var(--transition-fast);
  }

  .mobile-overlay.active {
    display: block;
    opacity: 1;
  }

  /* Tablet and Desktop */
  @media (min-width: 768px) {
    .nav-desktop {
      display: block;
    }

    .mobile-menu-btn {
      display: none;
    }
  }

  /* Mobile Navigation */
  @media (max-width: 767px) {
    .lang-select-web {
      display: none;
    }
    .lang-select {
      display: block;
    }
    .nav-desktop {
      display: block;
      position: fixed;
      right: 0;
      width: 0;
      top: 0;
      height: 100vh;
      background-color: var(--white);
      box-shadow: var(--shadow-lg);
      transition: all var(--transition-base);
      z-index: 45;
      overflow-y: auto;
    }

    .nav-desktop.nav-open {
      width: 280px;
    }

    .nav-list {
      flex-direction: column;
      gap: 0;
      padding: var(--space-6) var(--space-4);
    }

    .nav-item {
      min-width: fit-content;
      white-space: nowrap;
      border-bottom: 1px solid var(--gray-100);
    }

    .nav-link {
      display: block;
      padding: var(--space-4) 0;
      font-size: var(--text-base);
    }

    .container {
      padding: 0 var(--space-3);
    }
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    .header {
      height: 50px;
    }
    .logo-icon {
      width: 120px;
      height: 23px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .logo-text {
      font-size: var(--text-lg);
    }

    .header-content {
      height: 50px;
    }

    .nav-desktop {
      top: 0;
      height: 100vh;
    }
  }
</style>
